import $ from "jquery";
import * as preact from "preact";

import * as analytics from "analytics";
import * as chromeExtensionInstaller from "chromeExtensionInstaller";
import * as deviceDetect from "deviceDetect";
import * as onboarding from "onboarding";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import { cn } from "shared/utils";
import * as signupModal from "signupModal";
import Button from "ui/Button";
import * as walkthrough from "walkthrough";

/**
 * This allows us to present a button to the user allowing them to install Readlang as a PWA to
 * their home screen.
 */
let installPrompt: { prompt?: () => void } | undefined = undefined;
window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  installPrompt = event as { prompt?: () => void };
});

/** Where the main tabs are: the app's tab bar is at the bottom */
const tabsPosition = () => (deviceDetect.isIOSApp() ? "below" : "above");

interface Props {
  stores: any;
  appRouter: any;
}

interface ChecklistItem {
  name: string;
  achievement?: string;
  isComplete?: () => Promise<boolean>;
  isActive: boolean | (() => Promise<boolean>);
  renderBody: (view: WalkthroughChecklistView) => preact.ComponentChild;
  /** Buttons doing it, in place of the instructions (onboardingLibrary1) */
  renderActions?: (view: WalkthroughChecklistView) => preact.ComponentChild;
}

interface ProcessedItem {
  name: string;
  body: preact.ComponentChild;
  actions?: preact.ComponentChild;
  complete: boolean;
  active: boolean;
}

interface State {
  dismissible: boolean;
  items: ProcessedItem[] | null;
  hidden: boolean;
  /** Buttons rather than instructions, and a card (onboardingLibrary1) */
  withButtons: boolean;
}

class WalkthroughChecklistView extends preact.Component<Props, State> {
  state: State = {
    dismissible: false,
    items: null,
    hidden: false,
    withButtons: false,
  };

  private get checklistItems(): ChecklistItem[] {
    return [
      {
        name: "Choose language to learn",
        achievement: "setLanguages",
        isActive: true,
        renderBody: (view) => {
          const language =
            languageMap.map[
              view.props.stores.user.currentLearningLanguage.value
            ];
          return (
            <>
              Currently set to {language.english}, change or confirm this using
              the{" "}
              <strong
                class="currentLearningLanguageHint"
                onMouseEnter={() => $("#editionDropdown").addClass("glow")}
                onMouseLeave={() => $("#editionDropdown").removeClass("glow")}
              >
                I'm learning
              </strong>{" "}
              dropdown at the top of the page.
            </>
          );
        },
      },
      {
        name: "Read something",
        achievement: "translate",
        isActive: true,
        renderBody: (view) => {
          const language =
            languageMap.map[
              view.props.stores.user.currentLearningLanguage.value
            ];
          const starterStories = language.starterStories;
          return (
            <div style="margin-left:20px">
              {starterStories ? (
                <>
                  Read the starter story{" "}
                  <a href={`/library/${starterStories.easy}`}>Arrival</a> (easy)
                  or <a href={`/library/${starterStories.medium}`}>Vacation</a>{" "}
                  (medium)
                </>
              ) : (
                <>
                  Read from the{" "}
                  <strong
                    class="discoverHint"
                    onMouseEnter={() => $("#navDiscover").addClass("glow")}
                    onMouseLeave={() => $("#navDiscover").removeClass("glow")}
                  >
                    Discover
                  </strong>{" "}
                  tab {tabsPosition()}
                </>
              )}
            </div>
          );
        },
        renderActions: (view) => {
          const { starterStories } =
            languageMap.map[
              view.props.stores.user.currentLearningLanguage.value
            ];
          return starterStories ? (
            <>
              <Button kind="primary" href={`/library/${starterStories.easy}`}>
                Easy story
              </Button>
              <Button href={`/library/${starterStories.medium}`}>
                Harder story
              </Button>
            </>
          ) : (
            <Button kind="primary" href="/library">
              Find something to read
            </Button>
          );
        },
      },
      {
        name: "Get the full app experience",
        achievement: "standalone",
        isActive: async () => {
          return (
            !(await readlangClient.isLocalMode()) &&
            !deviceDetect.isIOSApp() &&
            (deviceDetect.is("iOS") || deviceDetect.is("android"))
          );
        },
        renderBody: (view) => {
          if (installPrompt) {
            return (
              <a href="#" onClick={view.installPWA}>
                Install Readlang to your Home Screen
              </a>
            );
          }
          if (deviceDetect.is("iOS")) {
            return (
              <ol style="margin-left:20px">
                <li>
                  Tap your browser's "Share" button <i class="icon-share"></i>{" "}
                  next to the address bar
                </li>
                <li>Choose "Add to Home Screen"</li>
                <li>Open Readlang from your iOS home screen</li>
              </ol>
            );
          }
          return (
            <ol style="margin-left:20px">
              <li>Open your browser's menu</li>
              <li>Choose "Add to home screen"</li>
              <li>Open Readlang from your Android home screen</li>
            </ol>
          );
        },
        // A button where the browser can install it, and the steps elsewhere
        renderActions: (view) =>
          installPrompt ? (
            <Button onClick={view.installPWA}>Install Readlang</Button>
          ) : undefined,
      },
      {
        name: "Upload your own text or e-book",
        achievement: "uploadText",
        isActive: true,
        renderBody: () => (
          <ol style="margin-left:20px">
            <li>
              Visit the{" "}
              <strong
                class="libraryHint"
                onMouseEnter={() => $("#navLibrary").addClass("glow")}
                onMouseLeave={() => $("#navLibrary").removeClass("glow")}
              >
                Library
              </strong>{" "}
              tab {tabsPosition()}
            </li>
            <li>
              {deviceDetect.isIOSApp() ? "Tap" : "Click"} the{" "}
              <strong>
                <i class="icon-circle-arrow-up"></i> Upload
              </strong>{" "}
              button
            </li>
          </ol>
        ),
        renderActions: () => (
          <Button href="/upload" icon="icon-circle-arrow-up">
            Upload a text
          </Button>
        ),
      },
      {
        name: deviceDetect.isIOSApp()
          ? "Read a web page"
          : "Use the Web Reader",
        achievement: "webReader",
        // Deferred until the user has experienced the read→translate loop
        // (mirrors the "Practice flashcards" gating), reducing the first
        // decision to a clean either/or.
        isActive: async () => walkthrough.hasAchievement("translate"),
        renderBody: (view) => {
          if (deviceDetect.isIOSApp()) {
            return (
              <div style="margin-left:20px">
                Open a website from the <strong>Discover</strong> tab below,
                then tap any word to translate it
              </div>
            );
          }
          const englishLanguage =
            view.props.stores.user.viewJSON.value
              .currentLearningLanguageEnglishString;
          return (
            <ol style="margin-left:20px">
              <li>
                <a href="#" onClick={view.installWebReader}>
                  Install the Web Reader
                </a>
              </li>
              <li>
                Visit any {englishLanguage} webpage (see{" "}
                <a href="/links">suggested sites</a>)
              </li>
              <li>Use the Web Reader on that page</li>
            </ol>
          );
        },
        renderActions: (view) =>
          deviceDetect.isIOSApp() ? (
            <Button href="/links" icon="icon-globe">
              Open a website
            </Button>
          ) : (
            <Button onClick={view.installWebReader}>
              Install the Web Reader
            </Button>
          ),
      },
      {
        name: "Practice flashcards",
        achievement: "flashcards",
        isActive: async () => walkthrough.hasAchievement("translate"),
        renderBody: () => (
          <div style="margin-left:20px">
            Open the{" "}
            <strong
              class="flashcardsHint"
              onMouseEnter={() => $("#navTest").addClass("glow")}
              onMouseLeave={() => $("#navTest").removeClass("glow")}
            >
              Practice
            </strong>{" "}
            tab {tabsPosition()}
          </div>
        ),
        renderActions: () => <Button href="/learn">Practice</Button>,
      },
    ];
  }

  componentDidMount() {
    walkthrough.events.on("new", this.handleWalkthroughNew);
    void this.computeState();
  }

  componentWillUnmount() {
    walkthrough.events.off("new", this.handleWalkthroughNew);
  }

  handleWalkthroughNew = () => {
    void this.computeState();
  };

  async computeState() {
    const dismissible = !(await readlangClient.isLocalMode());

    const items: ProcessedItem[] = [];
    for (const item of this.checklistItems) {
      items.push({
        name: item.name,
        body: item.renderBody(this),
        actions: item.renderActions?.(this),
        complete: item.isComplete
          ? await item.isComplete()
          : walkthrough.hasAchievement(item.achievement),
        active:
          typeof item.isActive === "function"
            ? await item.isActive()
            : item.isActive,
      });
    }

    reorderItems(items);

    const allComplete = items.every((item) => !item.active || item.complete);

    if (this.props.stores.user.state.value.hideWalkthrough) {
      analytics.trackEventWithProperties("walkthrough", {
        complete: allComplete,
        hidden: true,
      });
      this.setState({ dismissible, items, hidden: true });
      return;
    }

    analytics.trackEventWithProperties("walkthrough", {
      complete: allComplete,
    });

    if (allComplete && dismissible) {
      this.setState({ dismissible, items, hidden: true });
      return;
    }

    this.setState({
      dismissible,
      items,
      hidden: false,
      withButtons: onboarding.libraryOnboarding(),
    });
  }

  showSignupModal = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    signupModal.show({
      heading: "Let's get started!",
      stores: this.props.stores,
    });
  };

  installWebReader = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    // cspell: disable-next-line
    if (window.chrome && (window.chrome as any).webstore) {
      chromeExtensionInstaller.install();
    } else {
      this.props.appRouter.navigate("/webReader", { trigger: true });
    }
  };

  installPWA = async (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    await installPrompt?.prompt?.();
  };

  close = () => {
    this.setState({ hidden: true });
    this.props.stores.user.save({ hideWalkthrough: true });
  };

  render() {
    if (this.state.hidden) return null;
    if (this.state.items === null) return null;

    const items = this.state.items;
    const completedCount = items.filter(
      (item) => item.complete && item.active
    ).length;
    const totalCount = items.filter((item) => item.active).length;

    if (this.state.withButtons) {
      return this.renderCard(items, completedCount, totalCount);
    }

    return (
      <div class="dashboardPanel walkthroughChecklistView">
        <div class="callout">
          {this.state.dismissible && (
            <div class="closeButton" onClick={this.close}>
              <i class="icon-remove"></i>
            </div>
          )}

          <h2 style="margin-bottom: 20px;">
            Get started ({completedCount}/{totalCount})
          </h2>

          <table class="walkthroughChecklist">
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.name}
                  class={cn({ inactive: !item.complete && !item.active })}
                >
                  <td>
                    <i
                      class={cn({
                        "icon-check": item.complete,
                        "icon-check-empty": !item.complete,
                        "icon-large": true,
                      })}
                    ></i>
                  </td>
                  <td
                    class={cn({
                      checklistNameContainer: true,
                      complete: item.complete,
                    })}
                  >
                    <span class="checklistName">{item.name}</span>
                    {item.active && !item.complete && (
                      <div class="listItemContainer">{item.body}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  /** The onboardingLibrary1 experiment's checklist: a card of buttons */
  renderCard(
    items: ProcessedItem[],
    completedCount: number,
    totalCount: number
  ) {
    return (
      <div class="checklistCard">
        {this.state.dismissible && (
          <button class="closeButton" aria-label="Close" onClick={this.close}>
            <i class="icon-remove"></i>
          </button>
        )}
        <div class="checklistHeading">
          <img
            class="checklistRabbit"
            src="/content/landingPages/rabbit-welcome-cropped.webp"
            alt=""
            width="682"
            height="1254"
          />
          <div>
            <h2>Get started</h2>
            <p>
              {completedCount} of {totalCount} done
            </p>
            <progress value={completedCount} max={totalCount}></progress>
          </div>
        </div>
        <ol class="checklistSteps">
          {items
            .filter((item) => item.complete || item.active)
            .map((item) => (
              <li key={item.name} class={cn({ complete: item.complete })}>
                <i
                  class={item.complete ? "icon-ok" : undefined}
                  aria-hidden="true"
                ></i>
                <div>
                  <span class="checklistName">{item.name}</span>
                  {!item.complete && (
                    <div class="checklistActions">
                      {item.actions ?? item.body}
                    </div>
                  )}
                </div>
              </li>
            ))}
        </ol>
      </div>
    );
  }
}

function reorderItems(items: ProcessedItem[]) {
  let firstUncompletedIndex: number | undefined;
  for (let index = 0; index < items.length; index++) {
    const item = items[index];
    if (typeof firstUncompletedIndex === "undefined") {
      if (!item.complete) {
        firstUncompletedIndex = index;
      }
    } else {
      if (item.complete) {
        items.splice(index, 1);
        items.splice(firstUncompletedIndex, 0, item);
        firstUncompletedIndex++;
      }
    }
  }
}

export default WalkthroughChecklistView;
